import React, { useState, useEffect, useCallback } from 'react';
import { 
  Upload, 
  Image, 
  FileText, 
  X, 
  Check, 
  AlertTriangle, 
  Eye, 
  Trash2, 
  Plus,
  Camera,
  FolderOpen,
  Download,
  Grid,
  List,
  Search,
  Filter,
  Loader,
  CheckCircle,
  AlertCircle,
  Info
} from 'lucide-react';
import DocumentManager from './DocumentManager.js';
import documentAccessService from '../services/documentAccessService.js';

const isImageDocument = (document) => String(document?.mime_type || document?.file_type || '').startsWith('image/');

const SecureDocumentThumbnail = ({ document }) => {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    if (!document?.id || !isImageDocument(document)) return undefined;
    documentAccessService.getDocumentAccess(document.id)
      .then((access) => { if (active) setUrl(access.viewerUrl); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [document?.id]);

  if (!url || failed) return <Image className="w-8 h-8 text-gray-400" />;
  return (
    <img
      src={url}
      alt={document.original_name || document.name || 'Dokument'}
      className="w-full h-full object-cover"
      onError={() => setFailed(true)}
    />
  );
};

/**
 * Document Selector Widget for Clara360 Marketplace
 * Integrates with existing document system and provides photo upload
 */
const DocumentSelectorWidget = ({ 
  propertyId, 
  unitId, 
  onDocumentsSelected, 
  selectedDocuments = [],
  allowUpload = true,
  showStats = true,
  compact = false
}) => {
  const [documentManager] = useState(() => new DocumentManager());
  const [categorizedDocuments, setCategorizedDocuments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [documentStats, setDocumentStats] = useState(null);

  // Load documents on component mount
  useEffect(() => {
    if (propertyId) {
      loadDocuments();
    }
  }, [propertyId, unitId]);

  // Update stats when documents change
  useEffect(() => {
    if (categorizedDocuments && Object.keys(categorizedDocuments).length > 0) {
      const stats = documentManager.getDocumentStats(categorizedDocuments);
      setDocumentStats(stats);
    }
  }, [categorizedDocuments, documentManager]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const documents = await documentManager.getPropertyDocuments(propertyId, unitId);
      setCategorizedDocuments(documents);
    } catch (error) {
      console.error('Error loading documents:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (files, category = 'photos') => {
    try {
      setUploading(true);
      setUploadProgress({ completed: 0, total: files.length, currentFile: '' });

      const fileArray = Array.from(files);
      
      if (category === 'photos') {
        const { results, errors } = await documentManager.uploadMultiplePhotos(
          fileArray,
          propertyId,
          unitId,
          (progress) => setUploadProgress(progress)
        );

        if (errors.length > 0) {
          console.warn('Some uploads failed:', errors);
        }
      } else {
        // Handle single document upload
        for (const file of fileArray) {
          await documentManager.uploadDocument(file, propertyId, unitId, category, 'other');
        }
      }

      await loadDocuments();
      setShowUploadModal(false);
    } catch (error) {
      console.error('Upload error:', error);
      setError('Upload fehlgeschlagen: ' + error.message);
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      handleFileUpload(imageFiles, 'photos');
    }
  }, [propertyId, unitId]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDocumentSelect = (document) => {
    const isSelected = selectedDocuments.some(doc => doc.id === document.id);
    let newSelection;
    
    if (isSelected) {
      newSelection = selectedDocuments.filter(doc => doc.id !== document.id);
    } else {
      newSelection = [...selectedDocuments, document];
    }
    
    onDocumentsSelected(newSelection);
  };

  const handleDeleteDocument = async (documentId) => {
    if (!confirm('Dokument wirklich löschen?')) return;
    
    try {
      await documentManager.deleteDocument(documentId);
      await loadDocuments();
      
      // Remove from selection if selected
      const newSelection = selectedDocuments.filter(doc => doc.id !== documentId);
      onDocumentsSelected(newSelection);
    } catch (error) {
      console.error('Delete error:', error);
      setError('Löschen fehlgeschlagen: ' + error.message);
    }
  };

  // Filter documents based on search and category
  const getFilteredDocuments = () => {
    let allDocuments = [];
    
    Object.entries(categorizedDocuments).forEach(([categoryKey, category]) => {
      if (selectedCategory === 'all' || selectedCategory === categoryKey) {
        allDocuments = allDocuments.concat(
          category.documents.map(doc => ({ ...doc, categoryKey, categoryName: category.name }))
        );
      }
    });

    if (searchTerm) {
      allDocuments = allDocuments.filter(doc => 
        doc.original_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.categoryName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return allDocuments;
  };

  const DocumentCard = ({ document, isSelected, onSelect, onDelete, onPreview }) => (
    <div 
      className={`relative group bg-white dark:bg-gray-700 border-2 rounded-lg p-3 cursor-pointer transition-all ${
        isSelected 
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
      }`}
      onClick={() => onSelect(document)}
    >
      {/* Selection Indicator */}
      <div className={`absolute top-2 right-2 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
        isSelected 
          ? 'bg-blue-500 border-blue-500' 
          : 'border-gray-300 dark:border-gray-500 bg-white dark:bg-gray-600'
      }`}>
        {isSelected && <Check className="w-3 h-3 text-white" />}
      </div>

      {/* Document Preview */}
      <div className="aspect-square bg-gray-100 dark:bg-gray-600 rounded-lg mb-3 flex items-center justify-center overflow-hidden">
        {isImageDocument(document) ? (
          <SecureDocumentThumbnail document={document} />
        ) : (
          <FileText className="w-8 h-8 text-gray-400" />
        )}
      </div>

      {/* Document Info */}
      <div className="space-y-1">
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {documentManager.getDisplayName(document)}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {document.categoryName}
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500">
          {(document.file_size / 1024).toFixed(0)} KB
        </div>
      </div>

      {/* Action Buttons */}
      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center space-x-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPreview(document);
          }}
          className="bg-white text-gray-900 p-2 rounded-full hover:bg-gray-100"
        >
          <Eye className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(document.id);
          }}
          className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const DocumentList = ({ document, isSelected, onSelect, onDelete }) => (
    <div 
      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
        isSelected 
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
      }`}
      onClick={() => onSelect(document)}
    >
      {/* Selection Checkbox */}
      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mr-3 ${
        isSelected 
          ? 'bg-blue-500 border-blue-500' 
          : 'border-gray-300 dark:border-gray-500'
      }`}>
        {isSelected && <Check className="w-3 h-3 text-white" />}
      </div>

      {/* Document Icon */}
      <div className="w-10 h-10 bg-gray-100 dark:bg-gray-600 rounded flex items-center justify-center mr-3">
        {document.mime_type?.startsWith('image/') ? (
          <Image className="w-5 h-5 text-gray-500" />
        ) : (
          <FileText className="w-5 h-5 text-gray-500" />
        )}
      </div>

      {/* Document Info */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
          {documentManager.getDisplayName(document)}
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {document.categoryName} • {(document.file_size / 1024).toFixed(0)} KB
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center space-x-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            documentAccessService.openDocument(document.id).catch((error) => {
              console.error('Document preview failed:', error);
            });
          }}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <Eye className="w-4 h-4" />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(document.id);
          }}
          className="text-red-400 hover:text-red-600"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  const UploadModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Dokumente hochladen
          </h3>
          <button
            onClick={() => setShowUploadModal(false)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fotos hochladen
            </label>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileUpload(e.target.files, 'photos')}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          {/* Document Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Dokumente hochladen
            </label>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFileUpload(e.target.files, 'certificates')}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
            />
          </div>

          {/* Upload Progress */}
          {uploadProgress && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Upload läuft...
                </span>
                <span className="text-sm text-blue-700 dark:text-blue-300">
                  {uploadProgress.completed}/{uploadProgress.total}
                </span>
              </div>
              <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${(uploadProgress.completed / uploadProgress.total) * 100}%` }}
                />
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                {uploadProgress.currentFile}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-gray-600 dark:text-gray-400">Lade Dokumente...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
        <button
          onClick={loadDocuments}
          className="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  const filteredDocuments = getFilteredDocuments();
  const categories = Object.entries(categorizedDocuments);

  return (
    <div 
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg ${compact ? 'p-4' : 'p-6'}`}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <FolderOpen className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Dokumente & Fotos
          </h3>
        </div>
        
        {allowUpload && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Upload
          </button>
        )}
      </div>

      {/* Stats */}
      {showStats && documentStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {documentStats.totalPhotos}
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300">Fotos</div>
          </div>
          
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {documentStats.totalDocuments}
            </div>
            <div className="text-sm text-green-700 dark:text-green-300">Gesamt</div>
          </div>
          
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {documentStats.completeness}%
            </div>
            <div className="text-sm text-purple-700 dark:text-purple-300">Vollständig</div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3">
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              {selectedDocuments.length}
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300">Ausgewählt</div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Dokumente durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>

        {/* Category Filter */}
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
        >
          <option value="all">Alle Kategorien</option>
          {categories.map(([key, category]) => (
            <option key={key} value={key}>
              {category.icon} {category.name} ({category.count})
            </option>
          ))}
        </select>

        {/* View Mode */}
        <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400'}`}
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 dark:text-gray-400'}`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Drag & Drop Overlay */}
      {dragOver && (
        <div className="absolute inset-0 bg-blue-500 bg-opacity-20 border-2 border-dashed border-blue-500 rounded-lg flex items-center justify-center z-10">
          <div className="text-center">
            <Upload className="w-12 h-12 text-blue-600 mx-auto mb-2" />
            <p className="text-blue-600 font-medium">Fotos hier ablegen</p>
          </div>
        </div>
      )}

      {/* Documents Grid/List */}
      {filteredDocuments.length === 0 ? (
        <div className="text-center py-12">
          <Camera className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Keine Dokumente gefunden
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchTerm || selectedCategory !== 'all' 
              ? 'Versuchen Sie andere Suchkriterien.' 
              : 'Laden Sie Fotos und Dokumente für bessere Exposés hoch.'}
          </p>
          {allowUpload && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              Erste Dokumente hochladen
            </button>
          )}
        </div>
      ) : (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4' 
          : 'space-y-3'
        }>
          {filteredDocuments.map((document) => {
            const isSelected = selectedDocuments.some(doc => doc.id === document.id);
            
            return viewMode === 'grid' ? (
              <DocumentCard
                key={document.id}
                document={document}
                isSelected={isSelected}
                onSelect={handleDocumentSelect}
                onDelete={handleDeleteDocument}
                onPreview={(doc) => documentAccessService.openDocument(doc.id).catch((error) => {
                  console.error('Document preview failed:', error);
                })}
              />
            ) : (
              <DocumentList
                key={document.id}
                document={document}
                isSelected={isSelected}
                onSelect={handleDocumentSelect}
                onDelete={handleDeleteDocument}
              />
            );
          })}
        </div>
      )}

      {/* Selection Summary */}
      {selectedDocuments.length > 0 && (
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
              <span className="text-blue-900 dark:text-blue-100 font-medium">
                {selectedDocuments.length} Dokument{selectedDocuments.length !== 1 ? 'e' : ''} ausgewählt
              </span>
            </div>
            <button
              onClick={() => onDocumentsSelected([])}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 text-sm"
            >
              Auswahl zurücksetzen
            </button>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && <UploadModal />}
    </div>
  );
};

export default DocumentSelectorWidget;

